// Turning an API refusal into a sentence a person can act on. Many routes answer a validation failure
// with zod's `error.flatten()` — { formErrors: string[], fieldErrors: { field: string[] } } — and a page
// that put that object on screen either printed "API error 400" or crashed React outright ("Objects are
// not valid as a React child"). Pure: no React, no fetch.

const ACRONYMS = new Set(["url", "uri", "id", "ip", "idp", "sp", "api", "sso", "saml", "mfa", "dpa", "kev", "lei"]);

/** "logoUrl" → "Logo URL", "dayPence" → "Day pence", "hostname" → "Hostname". */
export function fieldLabel(key: string): string {
  const words = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.toLowerCase()));
  if (!words.length || !words[0]) return key;
  const first = words[0] === words[0].toUpperCase() ? words[0] : words[0][0].toUpperCase() + words[0].slice(1);
  return [first, ...words.slice(1)].join(" ");
}

/** zod's default wording, said plainly. Anything not recognised is passed through as written. */
export function plainValidationMessage(message: string): string {
  let m: RegExpMatchArray | null;
  if ((m = message.match(/^String must contain at most (\d+) character\(s\)$/))) return `${m[1]} characters at most`;
  if ((m = message.match(/^String must contain at least (\d+) character\(s\)$/))) return m[1] === "1" ? "required" : `at least ${m[1]} characters`;
  if ((m = message.match(/^Number must be less than or equal to (-?[\d.e+]+)$/))) return `must be ${Number(m[1]).toLocaleString("en-GB")} or less`;
  if ((m = message.match(/^Number must be greater than or equal to (-?[\d.e+]+)$/))) return `must be ${Number(m[1]).toLocaleString("en-GB")} or more`;
  if ((m = message.match(/^Number must be greater than (-?[\d.e+]+)$/))) return `must be more than ${Number(m[1]).toLocaleString("en-GB")}`;
  if ((m = message.match(/^Number must be less than (-?[\d.e+]+)$/))) return `must be less than ${Number(m[1]).toLocaleString("en-GB")}`;
  if (message === "Required") return "required";
  if (message === "Invalid email") return "enter a valid email address";
  if (message === "Invalid url") return "enter a valid web address";
  if (message === "Invalid" || message === "Invalid input") return "is not valid";
  if (/^Expected .+, received /.test(message)) return "is not valid";
  return message;
}

/**
 * The sentence for a zod flatten() payload, or null when `error` is not one. One line per field, at
 * most three, so a form never shows a wall of text: "Hostname: must be a fully qualified domain name".
 */
export function describeValidationError(error: unknown): string | null {
  if (!error || typeof error !== "object" || Array.isArray(error)) return null;
  let { formErrors, fieldErrors } = error as { formErrors?: unknown; fieldErrors?: unknown };
  if (!Array.isArray(formErrors) && (!fieldErrors || typeof fieldErrors !== "object")) {
    // Some routes send flatten().fieldErrors on its own: { field: ["message"] }.
    const values = Object.values(error as Record<string, unknown>);
    if (!values.length || !values.every((v) => Array.isArray(v) && v.every((x) => typeof x === "string"))) return null;
    formErrors = [];
    fieldErrors = error;
  }
  const parts: string[] = [];
  for (const f of Array.isArray(formErrors) ? formErrors : []) if (typeof f === "string" && f) parts.push(plainValidationMessage(f));
  for (const [key, msgs] of Object.entries((fieldErrors ?? {}) as Record<string, unknown>)) {
    const first = Array.isArray(msgs) ? msgs.find((x) => typeof x === "string" && x) : null;
    if (typeof first === "string") parts.push(`${fieldLabel(key)}: ${plainValidationMessage(first)}`);
  }
  if (!parts.length) return "Some of the details are not valid.";
  return parts.slice(0, 3).join(". ") + (parts.length > 3 ? ` (and ${parts.length - 3} more)` : "");
}
